import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title = "ManuscriptSmith - Write Like Yourself | AI Writing Studio for Authors",
  description = "AI writing studio that learns your voice and keeps your book KDP-ready - from first chapter to publish. For indie authors, ghostwriters & self-publishers.",
  keywords = "ai writing studio, author voice dna, write in your own voice, kdp preflight, kdp publishing checklist, ai book writing software, self-publishing tools, scrivener alternative",
  canonicalUrl = "https://manuscriptsmith.com",
  ogImage = "https://manuscriptsmith.com/og-image.jpg",
  structuredData = null,
  pageType = "website"
}) => {
  
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      // Organization Schema
      {
        "@type": "Organization",
        "@id": "https://manuscriptsmith.com/#organization",
        "name": "ManuscriptSmith",
        "url": "https://manuscriptsmith.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://manuscriptsmith.com/logo.png",
          "width": 300,
          "height": 100
        },
        "description": "AI writing studio that learns your voice and keeps your book KDP-ready",
        "founder": {
          "@type": "Person",
          "name": "Vitaly Kirkpatrick",
          "jobTitle": "Founder & CEO"
        },
        "foundingDate": "2024",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "support@manuscriptsmith.com",
          "availableLanguage": "English"
        },
        "sameAs": [
          "https://twitter.com/manuscriptsmith",
          "https://linkedin.com/company/manuscriptsmith"
        ]
      },
      // Website Schema
      {
        "@type": "WebSite",
        "@id": "https://manuscriptsmith.com/#website",
        "url": "https://manuscriptsmith.com",
        "name": "ManuscriptSmith",
        "description": "AI Writing Studio for Authors and Self-Publishers",
        "publisher": {
          "@id": "https://manuscriptsmith.com/#organization"
        }
      },
      // Software Application Schema
      {
        "@type": "SoftwareApplication",
        "@id": "https://manuscriptsmith.com/#software",
        "name": "ManuscriptSmith",
        "description": "AI writing studio with Author Voice DNA and live KDP Preflight for authors, ghostwriters, and self-publishers",
        "url": "https://manuscriptsmith.com",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web Browser",
        "offers": [
          {
            "@type": "Offer",
            "name": "Author",
            "price": "19",
            "priceCurrency": "USD",
            "description": "Unlimited books, full export, live KDP Preflight, Author Voice DNA"
          },
          {
            "@type": "Offer",
            "name": "Pro",
            "price": "49",
            "priceCurrency": "USD",
            "description": "Advanced AI, collaboration, audiobook add-on, and API access"
          }
        ],
        "author": {
          "@id": "https://manuscriptsmith.com/#organization"
        }
      }
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={pageType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="ManuscriptSmith" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:creator" content="@manuscriptsmith" />
      <meta property="twitter:site" content="@manuscriptsmith" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="ManuscriptSmith" />
      <meta name="publisher" content="ManuscriptSmith" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      {/* Geo Tags */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />

      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="ManuscriptSmith" />

      {/* Performance and Security */}
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="referrer" content="no-referrer-when-downgrade" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link rel="preconnect" href="https://images.unsplash.com" />

      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      {/* Removed apple-touch-icon to prevent 404 errors */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />

      {/* Theme Color */}
      <meta name="theme-color" content="#2e7d63" />
      <meta name="msapplication-TileColor" content="#2e7d63" />
    </Helmet>
  );
};

export default SEOHead;